import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  IconButton,
  LinearProgress,
  Card,
  CardContent,
  Chip,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Close as CloseIcon,
  Campaign as CampaignIcon,
  Speed as SpeedIcon,
  Security as SecurityIcon,
  Group as GroupIcon,
  Warning as WarningIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { EmailTemplate, Candidate } from '../types';
import emailService from '../services/emailService';

interface BulkEmailCampaignProps {
  open: boolean;
  onClose: () => void;
  candidates: Candidate[];
}

interface CampaignSettings {
  emailDelay: number;
  batchSize: number;
  maxDailyEmails: number;
  testMode: boolean;
}

interface CampaignStatus {
  isRunning: boolean;
  currentBatch: number;
  totalBatches: number;
  emailsSent: number;
  emailsFailed: number;
  progress: number;
  estimatedTimeRemaining?: string;
}

const BulkEmailCampaign: React.FC<BulkEmailCampaignProps> = ({
  open,
  onClose,
  candidates
}) => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [campaignName, setCampaignName] = useState('');
  const [candidateFilter, setCandidateFilter] = useState('Active');
  const [selectedCandidates, setSelectedCandidates] = useState<Candidate[]>([]);

  // Campaign settings with safe defaults
  const [settings, setSettings] = useState<CampaignSettings>({
    emailDelay: 45000, // 45 seconds between emails
    batchSize: 5,      // 5 emails per batch
    maxDailyEmails: 100, // Daily limit
    testMode: true     // Start in test mode for safety
  });

  const [campaignStatus, setCampaignStatus] = useState<CampaignStatus>({
    isRunning: false,
    currentBatch: 0,
    totalBatches: 0,
    emailsSent: 0,
    emailsFailed: 0,
    progress: 0
  });

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dailyEmailCount, setDailyEmailCount] = useState(0);

  const loadDailyEmailCount = () => {
    // In a real implementation, this would load from the bulk email service
    // For now, we'll simulate it
    setDailyEmailCount(0);
  };

  const filterCandidates = useCallback(() => {
    let filtered = candidates;

    switch (candidateFilter) {
      case 'Active':
        filtered = candidates.filter(c => c.status === 'Active');
        break;
      case 'New':
        filtered = candidates.filter(c => c.status === 'New' || c.status === 'Applied');
        break;
      case 'InProcess':
        filtered = candidates.filter(c => c.status === 'In Process' || c.status === 'Interviewed');
        break;
      case 'All':
        filtered = candidates;
        break;
      default:
        filtered = candidates.filter(c => c.status === candidateFilter);
    }

    setSelectedCandidates(filtered);
  }, [candidates, candidateFilter]);

  // Load email templates
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const response = await emailService.getEmailTemplates();
        if (response.success) {
          const bulkTemplates = response.data.filter(t =>
            t.templateCategory === 'Marketing' ||
            t.templateCategory === 'Bulk Email' ||
            t.templateCategory === 'Newsletter'
          );
          setTemplates(bulkTemplates.length > 0 ? bulkTemplates : response.data);
        }
      } catch (err) {
        console.error('Error loading email templates:', err);
      }
    };

    if (open) {
      loadTemplates();
      loadDailyEmailCount();
      filterCandidates();
    }
  }, [open, filterCandidates]);

  const handleSettingsChange = (field: keyof CampaignSettings, value: any) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const calculateEstimatedTime = () => {
    const totalEmails = selectedCandidates.length;
    const totalBatches = Math.ceil(totalEmails / settings.batchSize);
    const emailTime = totalEmails * (settings.emailDelay / 1000);
    const batchTime = (totalBatches - 1) * 300; // 5 minutes between batches
    const totalSeconds = emailTime + batchTime;

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  };

  const startCampaign = async () => {
    if (!selectedTemplate) {
      setError('Please select an email template');
      return;
    }

    if (selectedCandidates.length === 0) {
      setError('No candidates selected');
      return;
    }

    if (!campaignName.trim()) {
      setError('Campaign name is required');
      return;
    }

    try {
      setError(null);
      setCampaignStatus(prev => ({
        ...prev,
        isRunning: true,
        totalBatches: Math.ceil(selectedCandidates.length / settings.batchSize),
        progress: 0
      }));

      // In a production implementation, this would call the Node.js bulk email service
      // For now, we'll simulate the campaign execution with proper status updates

      const totalEmails = selectedCandidates.length;
      const totalBatches = Math.ceil(totalEmails / settings.batchSize);

      for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
        const startIdx = batchIndex * settings.batchSize;
        const endIdx = Math.min(startIdx + settings.batchSize, totalEmails);
        const batchCandidates = selectedCandidates.slice(startIdx, endIdx);

        setCampaignStatus(prev => ({
          ...prev,
          currentBatch: batchIndex + 1,
          progress: ((batchIndex + 1) / totalBatches) * 100
        }));

        // Simulate batch processing
        for (let i = 0; i < batchCandidates.length; i++) {
          const candidate = batchCandidates[i];

          try {
            if (settings.testMode) {
              // In test mode, just simulate the email send
              console.log(`[TEST MODE] Would send email to ${candidate.email}`);
              await new Promise(resolve => setTimeout(resolve, 1000)); // Quick simulation
            } else {
              // In production, this would use the bulk email service
              // Process the template with candidate variables
              const variables = await emailService.extractVariables(candidate);
              const processedEmail = await emailService.processTemplate(selectedTemplate, variables);

              // Send the processed email
              await emailService.sendEmail(processedEmail);

              // Wait the configured delay between emails
              if (i < batchCandidates.length - 1) {
                await new Promise(resolve => setTimeout(resolve, settings.emailDelay));
              }
            }

            setCampaignStatus(prev => ({
              ...prev,
              emailsSent: prev.emailsSent + 1
            }));

          } catch (emailError) {
            console.error(`Failed to send email to ${candidate.email}:`, emailError);
            setCampaignStatus(prev => ({
              ...prev,
              emailsFailed: prev.emailsFailed + 1
            }));
          }
        }

        // Wait between batches (except for the last batch)
        if (batchIndex < totalBatches - 1) {
          await new Promise(resolve => setTimeout(resolve, 300000)); // 5 minutes
        }
      }

      setCampaignStatus(prev => ({
        ...prev,
        isRunning: false,
        progress: 100
      }));

      const successCount = campaignStatus.emailsSent;
      const failCount = campaignStatus.emailsFailed;

      if (settings.testMode) {
        setSuccess(`TEST MODE: Campaign simulation completed. ${totalEmails} emails would have been sent.`);
      } else {
        setSuccess(`Campaign completed! ${successCount} emails sent successfully, ${failCount} failed.`);
      }

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Campaign failed');
      setCampaignStatus(prev => ({
        ...prev,
        isRunning: false
      }));
    }
  };

  const stopCampaign = () => {
    setCampaignStatus(prev => ({
      ...prev,
      isRunning: false
    }));
  };

  const resetCampaign = () => {
    setCampaignStatus({
      isRunning: false,
      currentBatch: 0,
      totalBatches: 0,
      emailsSent: 0,
      emailsFailed: 0,
      progress: 0
    });
    setError(null);
    setSuccess(null);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center">
            <CampaignIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Bulk Email Campaign</Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {campaignStatus.isRunning && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Campaign Running: {campaignName}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={campaignStatus.progress}
              sx={{ mb: 1 }}
            />
            <Typography variant="body2" color="text.secondary">
              Batch {campaignStatus.currentBatch} of {campaignStatus.totalBatches} •
              Sent: {campaignStatus.emailsSent} • Failed: {campaignStatus.emailsFailed}
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Box sx={{ display: 'grid', gap: 3 }}>
          {/* Campaign Settings */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Campaign Configuration
              </Typography>

              <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: '1fr 1fr' }}>
                <TextField
                  label="Campaign Name"
                  value={campaignName}
                  onChange={(e) => setCampaignName(e.target.value)}
                  required
                  disabled={campaignStatus.isRunning}
                />

                <FormControl disabled={campaignStatus.isRunning}>
                  <InputLabel>Email Template</InputLabel>
                  <Select
                    value={selectedTemplate?.id || ''}
                    onChange={(e) => {
                      const template = templates.find(t => t.id === e.target.value) || null;
                      setSelectedTemplate(template);
                    }}
                  >
                    {templates.map((template) => (
                      <MenuItem key={template.id} value={template.id}>
                        {template.templateName} ({template.templateCategory})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </CardContent>
          </Card>

          {/* Candidate Selection */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Target Recipients
              </Typography>

              <Box sx={{ mb: 2 }}>
                <FormControl sx={{ minWidth: 200 }} disabled={campaignStatus.isRunning}>
                  <InputLabel>Filter Candidates</InputLabel>
                  <Select
                    value={candidateFilter}
                    onChange={(e) => setCandidateFilter(e.target.value)}
                  >
                    <MenuItem value="Active">Active Candidates</MenuItem>
                    <MenuItem value="New">New Applications</MenuItem>
                    <MenuItem value="InProcess">In Process</MenuItem>
                    <MenuItem value="All">All Candidates</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Chip
                  icon={<GroupIcon />}
                  label={`${selectedCandidates.length} recipients selected`}
                  color="primary"
                />
                <Typography variant="body2" color="text.secondary">
                  Estimated time: {calculateEstimatedTime()}
                </Typography>
              </Box>

              {selectedCandidates.length > 10 && (
                <Typography variant="body2" color="text.secondary">
                  {selectedCandidates.slice(0, 10).map(c => `${c.firstName} ${c.lastName}`).join(', ')}
                  {selectedCandidates.length > 10 && ` and ${selectedCandidates.length - 10} more...`}
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Rate Limiting Settings */}
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SecurityIcon sx={{ mr: 1, color: 'warning.main' }} />
                <Typography variant="h6">
                  SMTP Protection Settings
                </Typography>
              </Box>

              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  These settings prevent your email account from being temporarily blocked by GoDaddy SMTP servers.
                  Recommended settings ensure reliable delivery without triggering spam filters.
                </Typography>
              </Alert>

              <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: '1fr 1fr' }}>
                <TextField
                  label="Delay Between Emails (seconds)"
                  type="number"
                  value={settings.emailDelay / 1000}
                  onChange={(e) => handleSettingsChange('emailDelay', parseInt(e.target.value) * 1000)}
                  disabled={campaignStatus.isRunning}
                  helperText="Minimum 30 seconds recommended"
                  InputProps={{
                    startAdornment: <SpeedIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  }}
                />

                <TextField
                  label="Batch Size"
                  type="number"
                  value={settings.batchSize}
                  onChange={(e) => handleSettingsChange('batchSize', parseInt(e.target.value))}
                  disabled={campaignStatus.isRunning}
                  helperText="Maximum 10 emails per batch"
                  inputProps={{ min: 1, max: 10 }}
                />

                <TextField
                  label="Daily Email Limit"
                  type="number"
                  value={settings.maxDailyEmails}
                  onChange={(e) => handleSettingsChange('maxDailyEmails', parseInt(e.target.value))}
                  disabled={campaignStatus.isRunning}
                  helperText={`Today: ${dailyEmailCount} sent`}
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.testMode}
                      onChange={(e) => handleSettingsChange('testMode', e.target.checked)}
                      disabled={campaignStatus.isRunning}
                    />
                  }
                  label="Test Mode (No emails sent)"
                />
              </Box>
            </CardContent>
          </Card>

          {/* Safety Warnings */}
          <Alert severity="info" icon={<InfoIcon />}>
            <Typography variant="body2">
              <strong>Email Delivery Best Practices:</strong><br />
              • Test mode allows you to verify the campaign without sending emails<br />
              • 45-second delays prevent SMTP server blocking<br />
              • Batch processing with 5-minute breaks maintains good sender reputation<br />
              • Daily limits prevent accidental bulk sending
            </Typography>
          </Alert>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, width: '100%', justifyContent: 'space-between' }}>
          <Button
            onClick={onClose}
            disabled={campaignStatus.isRunning}
          >
            {campaignStatus.isRunning ? 'Running...' : 'Close'}
          </Button>

          <Box sx={{ display: 'flex', gap: 1 }}>
            {campaignStatus.isRunning ? (
              <Button
                onClick={stopCampaign}
                color="error"
                startIcon={<WarningIcon />}
              >
                Stop Campaign
              </Button>
            ) : (
              <>
                {(campaignStatus.emailsSent > 0 || campaignStatus.emailsFailed > 0) && (
                  <Button onClick={resetCampaign}>
                    Reset
                  </Button>
                )}
                <Button
                  variant="contained"
                  onClick={startCampaign}
                  disabled={!selectedTemplate || selectedCandidates.length === 0 || !campaignName.trim()}
                  startIcon={settings.testMode ? <InfoIcon /> : <CampaignIcon />}
                  color={settings.testMode ? "info" : "primary"}
                >
                  {settings.testMode ? 'Run Test Campaign' : 'Start Campaign'}
                </Button>
              </>
            )}
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default BulkEmailCampaign;